import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "./axios"; // Your configured Axios instance
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";


// React Query Keys
export const signKeys = {
  all: ['signs'] as const,
  list: (page: number, per_page: number, params: Omit<SignQueryParams, 'page' | 'per_page'>) => 
    ['signs', 'list', page, per_page, params] as const,
  options: () => ['signs', 'options'] as const,
  /** One cache entry per (search term, page size). */
  optionsSearch: (search: string, limit: number) =>
    [...signKeys.options(), 'search', search, limit] as const,
  /**
   * Hydration of specific ids. Sorted before it becomes a key: [3,7] and [7,3]
   * ask the same question, and an unsorted key would fetch it twice.
   */
  optionsByIds: (ids: number[]) =>
    [...signKeys.options(), 'by-ids', [...ids].sort((a, b) => a - b).join(',')] as const,
  detail: (id: number) => ['signs', 'detail', id] as const,
  sign: (id: number) => [...signKeys.all, id] as const,
};

/**
 * A centre as it appears in a picker: just enough to label and identify it.
 */
export interface SignOption {
  id: number;
  name: string | null;
  centre_code: string | null;
}

/** Truncation report returned alongside every /sign/options page. */
export interface SignOptionsMeta {
  returned: number;
  limit: number;
  /** More centres match than were returned - the operator must narrow the term. */
  has_more: boolean;
  search: string | null;
}

interface SignOptionsPage {
  data: SignOption[];
  meta: SignOptionsMeta;
}

/** Page size asked of /sign/options. The API clamps at 200 regardless. */
export const SIGN_OPTIONS_PAGE_SIZE = 50;

/**
 * Shared reader for /sign/options. Tolerates a missing `meta` so a stale API
 * (or a cached response predating the search work) degrades to "no truncation
 * reported" instead of throwing inside a picker.
 */
const fetchSignOptions = async (
  params: Record<string, unknown>
): Promise<SignOptionsPage> => {
  const response = await axios.get('/sign/options', { params });
  const payload = response?.data?.data;
  const rows: SignOption[] = Array.isArray(payload) ? payload : [];
  const meta = response?.data?.meta;

  return {
    data: rows,
    meta: {
      returned: typeof meta?.returned === 'number' ? meta.returned : rows.length,
      limit: typeof meta?.limit === 'number' ? meta.limit : rows.length,
      has_more: Boolean(meta?.has_more),
      search: meta?.search ?? null,
    },
  };
};

/**
 * SERVER-SIDE centre search, for "assign to centre" and the centre filter.
 *
 * Use this - not `useGetAllSigns({ per_page: 1000 })`, and no longer the
 * fetch-everything `useGetSignOptions()` below.
 *
 * TWO BUGS, TWO ERAS
 * ------------------
 * /sign/list clamps per_page to 100 server-side (Services\Sign::getList), so
 * `useGetAllSigns({ per_page: 1000 })` was silently returning the first hundred
 * centres and dropping the rest. Nothing surfaced the truncation, so a pub could
 * not be assigned to any centre past the hundredth.
 *
 * The fix for that - an unpaginated /sign/options - then created the opposite
 * problem: the endpoint returned the WHOLE centre catalogue (~340 kB of JSON on
 * a real deployment) on every load of the pub screen, and the "Rattacher a un
 * centre" <Select> mounted one <MenuItem> per row. That is the several-second
 * freeze on opening the form. The payload was not the slow part on its own; the
 * thousands of DOM nodes were.
 *
 * The endpoint is now bounded and searchable, so a picker fetches only what the
 * operator typed - one small request per (debounced) term, cached per term.
 *
 * `keepPreviousData` matters more than it looks: without it, every new term
 * blanks the option list while its request is in flight, so the dropdown flickers
 * empty on each keystroke and an operator mid-scroll loses their place.
 *
 * @param search  already-debounced term; '' fetches the (cached) default page
 * @param options `enabled` lets a closed picker fetch nothing at all
 */
export const useSearchSignOptions = (
  search: string,
  options?: { enabled?: boolean; limit?: number }
) => {
  const limit = options?.limit ?? SIGN_OPTIONS_PAGE_SIZE;
  const term = search.trim();

  const { data, error, ...res } = useQuery({
    queryKey: signKeys.optionsSearch(term, limit),
    queryFn: () =>
      fetchSignOptions(term === '' ? { limit } : { search: term, limit }),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    /*
     * The untyped page is cached hard (it is identical for everyone and the API
     * caches it too); typed terms get a shorter window because they are cheap to
     * refetch and far more likely to be affected by a centre created moments ago.
     */
    staleTime: term === '' ? 5 * 60 * 1000 : 60 * 1000,
  });

  return {
    signOptions: data?.data ?? [],
    meta: data?.meta,
    hasMore: data?.meta?.has_more ?? false,
    error,
    ...res,
  };
};

/**
 * Resolve specific centre ids to labels.
 *
 * REQUIRED whenever a picker has a pre-selected value. The search endpoint
 * returns at most a page of matches, so a pub attached to centre #9312 has no
 * matching row in the default page - and a MUI Autocomplete whose `value` is not
 * present in `options` renders BLANK. The operator then sees an empty "Rattacher
 * a un centre" field on a pub that is, in fact, attached, and the obvious
 * reaction is to pick something else: a silent reassignment caused purely by a
 * rendering gap.
 *
 * Kept as its own query rather than merged into the search: its lifetime is the
 * selection's, not the search term's, and merging them would evict the label
 * every time the operator typed.
 */
export const useSignOptionsByIds = (ids: number[]) => {
  const wanted = Array.from(new Set(ids.filter((id) => Number.isFinite(id) && id > 0)));

  const { data, error, ...res } = useQuery({
    queryKey: signKeys.optionsByIds(wanted),
    queryFn: () => fetchSignOptions({ ids: wanted }),
    enabled: wanted.length > 0,
    // A centre's name is not volatile; refetching it on every mount would add a
    // request per modal open for a string that changes once a year.
    staleTime: 10 * 60 * 1000,
  });

  return { signOptions: data?.data ?? [], error, ...res };
};

/**
 * @deprecated Fetches only the first page of centres, NOT all of them.
 *
 * Kept so nothing breaks on import, but it is no longer "every centre": since
 * /sign/options became bounded, this returns the default page (50) and silently
 * drops the rest - the exact truncation bug it was originally written to fix,
 * just at a different cut-off. Any picker must use `useSearchSignOptions` +
 * `useSignOptionsByIds` instead.
 */
export const useGetSignOptions = () => {
  const { data, error, ...res } = useQuery({
    queryKey: signKeys.options(),
    queryFn: async (): Promise<SignOption[]> => {
      const page = await fetchSignOptions({ limit: SIGN_OPTIONS_PAGE_SIZE });
      return page.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  return { signOptions: data ?? [], error, ...res };
};

// Fetch all signs (if you want a list endpoint later)
export const useGetAllSigns = (params: SignQueryParams = {}) => {
  const { page = 1, per_page = 10, ...queryParams } = params;
  
  const { data, error, ...res } = useQuery({
    queryKey: signKeys.list(page, per_page, queryParams),
    queryFn: async (): Promise<{ data: TSign[], pagination: any }> => {
      // Using query parameters for everything
      const response = await axios.get('/sign/list', { 
        params: { page, per_page, ...queryParams }
      });
      console.log(response);
      return {
        data: response?.data.data,
        pagination: response?.data.pagination
      };
    },
  });

  return { 
    signs: data?.data ?? [], 
    pagination: data?.pagination,
    error, 
    ...res 
  };
};

// Get one sign by ID
export const useGetSign = (id: number) => {
  return useQuery<TSign>({
    queryKey: signKeys.sign(id),
    queryFn: async () => {
      const res = await axios.get(`/sign/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
};

// Create a sign
export const useCreateSign = (
  onClose?: () => void,
  initSign?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (newSign: RequestSign) => {
      const response = await axios.post("/sign/add", newSign);
      return response.data;
    },
    onSuccess: () => {
      onClose && onClose();
      initSign && initSign();
      
      // Invalidate all sign-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: signKeys.all });
      
      toast.success(t("sign.createSuccess", "Sign created successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("sign.unauthorized", "You are not authorized to create signs"));
      } else if (err?.response?.data?.code === "23000") {
        toast.error(t("sign.alreadyExists", "Sign with this name already exists"));
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("sign.createError", "Error creating sign"));
      }
    },
  });
};

// Update a sign
export const useUpdateSign = (
  signId: number,
  onClose?: () => void,
  initSign?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updatedSign: RequestSign) => {
      const response = await axios.put(`/sign/update/${signId}`, updatedSign);
      return response.data;
    },
    onSuccess: () => {
      onClose && onClose();
      initSign && initSign();
      
      // Invalidate both the specific sign and all sign lists
      queryClient.invalidateQueries({ queryKey: signKeys.detail(signId) });
      queryClient.invalidateQueries({ queryKey: signKeys.all });
      
      toast.success(t("sign.updateSuccess", "Sign updated successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("sign.unauthorizedUpdate", "You are not authorized to update this sign"));
      } else if (err?.response?.data?.code === "23000") {
        toast.error(t("sign.nameExists", "A sign with this name already exists"));
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("sign.updateError", "Error updating sign"));
      }
    },
  });
};

// Delete a sign
export const useDeleteSign = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (signId: number) => {
      const response = await axios.delete(`/sign/delete/${signId}`);
      return response.data;
    },
    onSuccess: () => {
      onSuccess && onSuccess();
      
      // Invalidate all sign-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: signKeys.all });
      
      toast.success(t("sign.deleteSuccess", "Sign deleted successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("sign.unauthorizedDelete", "You are not authorized to delete this sign"));
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("sign.deleteError", "Error deleting sign"));
      }
    },
  });
};

// Attach services to a sign
export const useAttachServices = (signId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (serviceIds: number[]) => {
      const response = await axios.post(`/sign/attach-service/${signId}`, {
        service_ids: serviceIds
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the specific sign query to reflect the changes
      queryClient.invalidateQueries({ queryKey: signKeys.sign(signId) });
      queryClient.invalidateQueries({ queryKey: signKeys.detail(signId) });
      
      toast.success(t("sign.servicesAttached", "Services attached successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("sign.unauthorized", "You are not authorized to modify this sign"));
      } else if (err?.response?.data?.errors) {
        // Handle validation errors from the detachService method
        const errors = err.response.data.errors;
        Object.values(errors).forEach((errorArray: any) => {
          errorArray.forEach((error: string) => {
            toast.error(error);
          });
        });
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("sign.attachServicesError", "Error attaching services"));
      }
    },
  });
};

// Detach services from a sign
export const useDetachServices = (signId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (serviceIds: number[]) => {
      const response = await axios.post(`/sign/detach-service/${signId}`, {
        service_ids: serviceIds
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the specific sign query to reflect the changes
      queryClient.invalidateQueries({ queryKey: signKeys.sign(signId) });
      queryClient.invalidateQueries({ queryKey: signKeys.detail(signId) });
      
      toast.success(t("sign.servicesDetached", "Services detached successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("sign.unauthorized", "You are not authorized to modify this sign"));
      } else if (err?.response?.data?.errors) {
        // Handle validation errors from the detachService method
        const errors = err.response.data.errors;
        Object.values(errors).forEach((errorArray: any) => {
          errorArray.forEach((error: string) => {
            toast.error(error);
          });
        });
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("sign.detachServicesError", "Error detaching services"));
      }
    },
  });
};

// Assign services to multiple signs
export const useAssignServicesToSigns = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { sign_ids: number[], service_ids: number[] }) => {
      const response = await axios.post('/signs/assign-services', data);
      return response.data;
    },
    onSuccess: () => {
      onSuccess && onSuccess();
      
      // Invalidate all sign-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: signKeys.all });
      
      toast.success(t("sign.assignServicesSuccess", "Services assigned to signs successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("sign.unauthorized", "You are not authorized to assign services"));
      } else if (err?.response?.data?.errors) {
        // Handle validation errors
        const errors = err.response.data.errors;
        Object.values(errors).forEach((errorArray: any) => {
          errorArray.forEach((error: string) => {
            toast.error(error);
          });
        });
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("sign.assignServicesError", "Error assigning services to signs"));
      }
    },
  });
};

// Like or unlike a sign
export const useLikeOrUnlikeSign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (signId: number) => {
      const res = await axios.get(`/sign/like/${signId}`);
      return res.data;
    },
    onSuccess: (_, signId) => {
      queryClient.invalidateQueries({ queryKey: signKeys.sign(signId) });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Error liking/unliking sign");
    },
  });
};